import { ForeAftEnum, TContainerLengths } from "open-vessel-definition";
import {
  ILcgsTriplet,
  TAllBayLcgs,
} from "./BayLcgVcgTcgAndPairings/calculateMissingLcgs";
import { feetToMillimeters, pad3 } from "@tedivo/tedivo-pure-helpers";

import { BaySizesAndCgs } from "./BayLcgVcgTcgAndPairings/BaySizesAndCgs";
import { I20LcgsByBay } from "./types/I20Lcgs";
import { IBayPattern } from "./types/IPositionPatterns";
import { ICellCGsInfo } from "./BayLcgVcgTcgAndPairings/CellInfo";
import { IGetBayBlocksOutput } from "./types/IGetBayBlocksOutput";

/**
 * Given a set of LCGs of different sizes, find the LCG for every 20 bay
 * @param sizeSummary
 * @param bayLevelSizesAndCgsAbove
 * @param bayLevelSizesAndCgsBelow
 * @param missingImportantLcgs boolean
 * @returns An object that maps IsoBayNumber to 20's LCG. It doesn't differentiate between ABOVE and BELOW
 */
export default function generate20Lcgs({
  maxIsoBay,
  lcgByBayAbove,
  lcgByBayBelow,
  missingImportantLcgs,
  missingLcgs,
  blockBaysAndSizesBy20Bay,
}: {
  maxIsoBay: number;
  lcgByBayAbove: BaySizesAndCgs[];
  lcgByBayBelow: BaySizesAndCgs[];
  missingImportantLcgs: boolean;
  missingLcgs: TAllBayLcgs;
  blockBaysAndSizesBy20Bay: IGetBayBlocksOutput["blockBaysAndSizesBy20Bay"];
}): { lcgsBy20Bay: I20LcgsByBay; minLcgs20: number; maxLcgs20: number } {
  // Return object
  const lcgsBy20Bay: I20LcgsByBay = {};
  let minLcgs20 = Infinity;
  let maxLcgs20 = -Infinity;

  const allBlps = [...lcgByBayAbove, ...lcgByBayBelow];

  // Initialize
  for (let iBay = 1; iBay <= maxIsoBay; iBay += 2) {
    const bay = pad3(iBay);
    const block20Info = blockBaysAndSizesBy20Bay[bay];

    lcgsBy20Bay[bay] = {
      maxSize: 20,
      confirmPairedBay: block20Info?.allBays
        .split("-")
        .find((b) => b !== bay) as IBayPattern | undefined,
      lcgs: new Map<TContainerLengths, ILcgsTriplet>(),
      maxForeLcg: -Infinity,
      minAftLcg: Infinity,
    };
  }

  allBlps.forEach((bl) => {
    const maxSize = bl.maxSize || 20;
    const missingLcgsOfBay = missingLcgs[bl.isoBay];

    const objLcgs = getLcgs(
      missingImportantLcgs,
      missingLcgsOfBay,
      bl.getAvgCGs(),
    );

    if (lcgsBy20Bay[bl.isoBay].paired === undefined)
      lcgsBy20Bay[bl.isoBay].paired = bl.pairedBay;

    if (maxSize > lcgsBy20Bay[bl.isoBay].maxSize)
      lcgsBy20Bay[bl.isoBay].maxSize = maxSize;

    if (objLcgs?.size) {
      objLcgs.forEach((lcgs, size) => {
        lcgsBy20Bay[bl.isoBay].lcgs.set?.(size, lcgs);

        // Global min and max
        if (lcgs.aftLcg < minLcgs20) minLcgs20 = lcgs.aftLcg;
        if (lcgs.foreLcg > maxLcgs20) maxLcgs20 = lcgs.foreLcg;

        // Local min and max
        if (lcgs.aftLcg < lcgsBy20Bay[bl.isoBay].minAftLcg)
          lcgsBy20Bay[bl.isoBay].minAftLcg = lcgs.aftLcg;
        if (lcgs.foreLcg > lcgsBy20Bay[bl.isoBay].maxForeLcg)
          lcgsBy20Bay[bl.isoBay].maxForeLcg = lcgs.foreLcg;
      });

      if (!lcgsBy20Bay[bl.isoBay].lcgs.has(20) && bl.pairedBay !== undefined) {
        const dif20 =
          feetToMillimeters((maxSize - 20) * 0.5) *
          (bl.pairedBay === ForeAftEnum.FWD ? -1 : 1);

        const lcgMaxSize = lcgsBy20Bay[bl.isoBay].lcgs.get(maxSize)?.lcg;

        if (lcgMaxSize) {
          lcgsBy20Bay[bl.isoBay].lcgs.set?.(20, {
            lcg: lcgMaxSize + dif20,
            aftLcg: lcgMaxSize + dif20 - feetToMillimeters(10),
            foreLcg: lcgMaxSize + dif20 + feetToMillimeters(10),
          });
        }

        //
      }
    }
  });

  // Is there a 40 bay at the end that is not paired or it is paired with AFT?
  // If so, we need to create a 40 bay paired with FWD
  let finalMaxIsoBay = maxIsoBay;
  const blpsEnd = allBlps.filter((bl) => bl.isoBay === pad3(maxIsoBay));
  if (
    blpsEnd.some((bl) => bl.maxSize >= 40 && bl.pairedBay !== ForeAftEnum.FWD)
  ) {
    finalMaxIsoBay += 2;
    lcgsBy20Bay[pad3(maxIsoBay)].paired = ForeAftEnum.AFT;
    lcgsBy20Bay[pad3(maxIsoBay)].confirmPairedBay = pad3(finalMaxIsoBay);
    lcgsBy20Bay[pad3(finalMaxIsoBay)] = {
      maxSize: lcgsBy20Bay[pad3(maxIsoBay)].maxSize || 40,
      paired: ForeAftEnum.FWD,
      confirmPairedBay: pad3(maxIsoBay),
      lcgs: copy40Lcgs(lcgsBy20Bay[pad3(maxIsoBay)].lcgs),
      maxForeLcg: lcgsBy20Bay[pad3(maxIsoBay)].maxForeLcg,
      minAftLcg: lcgsBy20Bay[pad3(maxIsoBay)].minAftLcg,
    };
  }

  // If there aren't slots defined we need to complete the 20CGs using the paired bay
  for (let iBay = 1; iBay <= finalMaxIsoBay; iBay += 2) {
    const bay = pad3(iBay);
    const b = lcgsBy20Bay[bay];

    if (b.confirmPairedBay && b.lcgs.size === 0) {
      const pairedBayLcgs = lcgsBy20Bay[b.confirmPairedBay];
      if (pairedBayLcgs) {
        b.maxForeLcg = pairedBayLcgs.maxForeLcg;
        b.minAftLcg = pairedBayLcgs.minAftLcg;
      }
    } else if (b.lcgs.size === 0) {
      const candFwd = lcgsBy20Bay[pad3(iBay - 2)];
      const candAft = lcgsBy20Bay[pad3(iBay + 2)];

      if (candFwd?.paired === ForeAftEnum.AFT) {
        b.paired = ForeAftEnum.FWD;
        b.confirmPairedBay = pad3(iBay - 2);
        const pairedBayLcgs = lcgsBy20Bay[b.confirmPairedBay];
        if (pairedBayLcgs) {
          pairedBayLcgs.confirmPairedBay = pad3(iBay);
          b.maxForeLcg = pairedBayLcgs.maxForeLcg;
          b.minAftLcg = pairedBayLcgs.minAftLcg;
        }
      } else if (candAft?.paired === ForeAftEnum.FWD) {
        b.paired = ForeAftEnum.AFT;
        b.confirmPairedBay = pad3(iBay + 2);
        const pairedBayLcgs = lcgsBy20Bay[b.confirmPairedBay];
        if (pairedBayLcgs) {
          pairedBayLcgs.confirmPairedBay = pad3(iBay);
          b.maxForeLcg = pairedBayLcgs.maxForeLcg;
          b.minAftLcg = pairedBayLcgs.minAftLcg;
        }
      }
    }
  }

  return { lcgsBy20Bay, minLcgs20, maxLcgs20 };
}

function getLcgs(
  missingImportantLcgs: boolean,
  missingLcgsOfBay: Map<Partial<TContainerLengths>, ILcgsTriplet> | undefined,
  avgCellCgs: Map<TContainerLengths, ICellCGsInfo> | undefined,
): Map<Partial<TContainerLengths>, ILcgsTriplet> | undefined {
  if (missingImportantLcgs) return missingLcgsOfBay;

  if (!avgCellCgs) return undefined;

  const lcgs = new Map<Partial<TContainerLengths>, ILcgsTriplet>();

  avgCellCgs.forEach((cgs, size) => {
    if (cgs.lcg !== undefined) {
      const halfSizeInMM = feetToMillimeters(size / 2);
      lcgs.set(size, {
        lcg: cgs.lcg,
        aftLcg: cgs.lcg - halfSizeInMM,
        foreLcg: cgs.lcg + halfSizeInMM,
      });
    }
  });

  return lcgs;
}

function copy40Lcgs(
  src: Map<Partial<TContainerLengths>, ILcgsTriplet>,
): Map<Partial<TContainerLengths>, ILcgsTriplet> {
  const lcgs = new Map<Partial<TContainerLengths>, ILcgsTriplet>();

  src.forEach((lcg, size) => {
    if (size >= 40) lcgs.set(size, { ...lcg });
  });

  return lcgs;
}
